import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  ArticleContent,
  ArticleOnTopTitle,
  BasicQuote,
  ContentWithoutSidebar,
  DividerWithQuoteIcon,
  ErrorBoundary,
  HoverAuthorsWithRoundImage,
  HUGE_SCREEN_SIZE,
  ImageWithCaptionAndIcon,
  OneOTCThreeVC,
  PostWithoutSidebarMainContent,
  SafeArticleContentWrapper,
  SecondarySectionTitle,
  SectionHeader,
  SectionWithTitle,
  Sponsor,
  Slideshow,
} from 'mm-ui-components';
import { Store } from 'redux';
import { StyleSheet } from 'aphrodite/no-important';
import { AdditionalStyleSheet } from 'mm-ui-components/dist/src/base/components.utils';
import { BlocksWithMultipleOptions } from 'mm-ui-components/dist/src/base/components/pageLayouts/BlockTypes.utils';
import { Theme } from '../../components/connectedComponents/Theme';
import { PostMetaTags } from '../../components/connectedComponents/PostMetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import {
  getAuthors,
  getBody,
  getCover,
  getCreatedAtDateFormatted,
  getCreatedAtTimeAndDateFormatted,
  getShareConfig,
  getSlideshowData,
  getSponsor,
  getTitle,
} from '../../store/template/postPage/postPage.selectors';
import { getAdSlotId, getCanonicalUrl, getSiteName, getTemplateName } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import {
  getRecommendationsArticles,
  getRecommendationsTitle,
} from '../../store/template/postPageKyiv/postPageKyiv.selectors';
import { createBlocksWidth } from '../postPage.util';
import { SLOT_NAMES } from '../templates.const';
import { DefaultNavigationLayout } from '../../components/connectedComponents/DefaultNavigationLayout';

interface PostPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const blocksWithMultipleOptions: BlocksWithMultipleOptions = {
  divider: DividerWithQuoteIcon,
  quote: BasicQuote,
  image: ImageWithCaptionAndIcon,
};

const createStyle = () => StyleSheet.create({
  style: {
    margin: '0 auto',
  },
}) as AdditionalStyleSheet;

export const PostPageComponent: React.FunctionComponent<PostPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <PostPageContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

export const PostPageContentConnected: React.FunctionComponent = () => {
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const title = useSelector(getTitle);
  const cover = useSelector(getCover);
  const body = useSelector(getBody);
  const shareConfig = useSelector(getShareConfig);
  const canonical = useSelector(getCanonicalUrl);
  const templateName = useSelector(getTemplateName);
  const siteName = useSelector(getSiteName);
  const sponsor = useSelector(getSponsor);
  const recommendationsSectionTitle = useSelector(getRecommendationsTitle);
  const recommendationsSectionArticles = useSelector(getRecommendationsArticles);
  const authors = useSelector(getAuthors);
  const createdAtDateFormatted = useSelector(getCreatedAtDateFormatted);
  const createdAtTimeAndDateFormatted = useSelector(getCreatedAtTimeAndDateFormatted);
  const slideshow = useSelector(getSlideshowData);

  const additionalStyle = createStyle();
  const defaultBlocksWidths = createBlocksWidth(740, 680, 640);
  const overrideBlocksWidths = {
    image: createBlocksWidth(880, 1080, 680),
  };

  const slideshowBlockWidth = createBlocksWidth(920, 680, 640);

  const overrideBlockPadding = { largePadding: 0, mediumPadding: 0, smallPadding: 0 };

  const shouldShowRecommendationsSection = recommendationsSectionArticles && recommendationsSectionArticles.length > 0;
  const articleContentWrapperHeight = authors ? 10 * authors.length : 0;

  const hasSlideshow = (slideshow && slideshow.totalSlides > 0) || false;
  const authorMarginTop = hasSlideshow ? 1 : undefined;

  return (
    <React.Fragment>
      <PostMetaTags />
      <HeadScripts />
      <Theme>
        <AppContent>
          <DefaultNavigationLayout mainWrapperConfigThemeKey="mainArticleContent">
            <ContentWithoutSidebar maxWidth={HUGE_SCREEN_SIZE} mediumWidth={880}>
              <PostWithoutSidebarMainContent>
                <ArticleOnTopTitle
                  {...cover}
                  title={title}
                  siteName={siteName}
                  templateName={templateName}
                  shareConfig={shareConfig}
                  canonical={canonical}
                />
                {sponsor ? <Sponsor {...sponsor} /> : null}
                <SafeArticleContentWrapper minHeightFactor={articleContentWrapperHeight}>
                  <Slideshow {...slideshow} blockWidths={slideshowBlockWidth}>
                    <ArticleContent
                      blocksWithMultipleOptions={blocksWithMultipleOptions}
                      body={body}
                      templateName={templateName}
                      siteName={siteName}
                      defaultBlocksWidths={defaultBlocksWidths}
                      overrideBlocksWidths={overrideBlocksWidths}
                      blocksPaddingOverride={overrideBlockPadding}
                    >
                      <HoverAuthorsWithRoundImage
                        authors={authors}
                        dateFormatted={createdAtDateFormatted}
                        timeAndDateFormatted={createdAtTimeAndDateFormatted}
                        marginTopFactor={authorMarginTop}
                      />
                    </ArticleContent>
                  </Slideshow>
                </SafeArticleContentWrapper>
              </PostWithoutSidebarMainContent>
              {shouldShowRecommendationsSection && (
                <ErrorBoundary>
                  <SectionWithTitle additionalStyle={additionalStyle}>
                    <SectionHeader moreButtonSvg={null} moreButtonText={null}>
                      <SecondarySectionTitle>{recommendationsSectionTitle}</SecondarySectionTitle>
                    </SectionHeader>
                    <OneOTCThreeVC cards={recommendationsSectionArticles} />
                  </SectionWithTitle>
                </ErrorBoundary>
              )}
            </ContentWithoutSidebar>
            <Ad id={oneOnOneId} />
          </DefaultNavigationLayout>
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
